// The masthead constellation: the page's own store locations drawn as glowing
// cyan points on a plain 2D canvas — no basemap, no MapLibre. The geography's
// silhouette emerges from the stores themselves, so every page gets a hero
// image made of its own data. Pure decoration (aria-hidden, behind a scrim);
// the payload fetch is shared with the store map further down the page.
import { fetchPayload } from "./payload_cache"

const GLOW = "124, 226, 255" // glacier cyan #7CE2FF
const REVEAL_MS = 1400

export function initTrendsHeros() {
  document.querySelectorAll("[data-trends-hero]").forEach((el) => {
    mountHero(el).catch(() => {})
  })
}

async function mountHero(el) {
  const payload = await fetchPayload(el.dataset.url)
  if (!payload || !payload.dots || payload.dots.length === 0) return

  const canvas = document.createElement("canvas")
  canvas.setAttribute("aria-hidden", "true")
  canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;"
  el.appendChild(canvas)

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  let cancel = null
  const draw = (animate) => {
    if (cancel) cancel()
    cancel = render(el, canvas, payload, animate && !reduced)
  }

  draw(true)
  new ResizeObserver(() => draw(false)).observe(el)
}

// Web-Mercator projection of [lon, lat] — both axes in radians so x and y
// share a unit and the geography keeps its true aspect.
function project([lon, lat]) {
  return [(lon * Math.PI) / 180, Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))]
}

// Draws the constellation; returns a cancel function while a staggered reveal
// is in flight, null once everything is on screen.
function render(el, canvas, payload, animate) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const w = el.clientWidth
  const h = el.clientHeight
  if (!w || !h) return null

  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  const ctx = canvas.getContext("2d")
  ctx.scale(dpr, dpr)

  // Fit the bbox into the canvas (contain), weighted toward the right half on
  // wide viewports so the headline column stays over quiet space.
  const [minLon, minLat, maxLon, maxLat] = payload.bbox
  const [x0, y0] = project([minLon, minLat])
  const [x1, y1] = project([maxLon, maxLat])
  const pad = 24
  const fitW = w >= 1024 ? w * 0.58 : w
  const scale = Math.min((fitW - 2 * pad) / (x1 - x0), (h - 2 * pad) / (y1 - y0))
  const offX = w - pad - (x1 - x0) * scale - (w >= 1024 ? w * 0.02 : (fitW - 2 * pad - (x1 - x0) * scale) / 2)
  const offY = (h - (y1 - y0) * scale) / 2

  const pts = []
  for (const [lon, lat] of payload.dots) {
    const [mx, my] = project([lon, lat])
    const x = offX + (mx - x0) * scale
    const y = offY + (y1 - my) * scale
    if (x >= -4 && x <= w + 4 && y >= -4 && y <= h + 4) pts.push([x, y])
  }
  if (!pts.length) return null

  // Fewer stores → bigger, brighter points; a metro's 400 dots should glow
  // like a constellation, a state's 25k like a nightscape — the top tier
  // dims each point so dense geographies don't bloom into a solid wash.
  // Keyed on the page's store count (data-stores), not on the dots: the
  // payload ships one dot per painted pixel, so a chain's 16k stores can
  // arrive as 7k dots and would read as a sparser page than it is.
  const n = Number(el.dataset.stores) || pts.length
  const core = n > 8000 ? 0.8 : n > 2000 ? 1.1 : n > 800 ? 1.5 : n > 200 ? 2 : 2.6
  const coreAlpha = n > 8000 ? 0.35 : n > 2000 ? 0.5 : 0.65
  const haloAlpha = n > 8000 ? 0.035 : n > 2000 ? 0.06 : 0.1

  const dot = ([x, y]) => {
    ctx.beginPath()
    ctx.fillStyle = `rgba(${GLOW}, ${haloAlpha})`
    ctx.arc(x, y, core * 3, 0, 2 * Math.PI)
    ctx.fill()
    ctx.beginPath()
    ctx.fillStyle = `rgba(${GLOW}, ${coreAlpha})`
    ctx.arc(x, y, core, 0, 2 * Math.PI)
    ctx.fill()
  }

  if (!animate) {
    pts.forEach(dot)
    return null
  }

  // Staggered reveal: draw progressively (payload order) without clearing.
  const perFrame = Math.max(8, Math.ceil(pts.length / (REVEAL_MS / 16)))
  let i = 0
  let raf = 0
  const step = () => {
    for (const end = Math.min(i + perFrame, pts.length); i < end; i++) dot(pts[i])
    if (i < pts.length) raf = requestAnimationFrame(step)
  }
  raf = requestAnimationFrame(step)
  return () => cancelAnimationFrame(raf)
}
